import React, { memo, useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Chip,
  CircularProgress,
} from '@mui/material';
import { ExportButton } from './ExportButton';
import { DateRangeFilter } from './DateRangeFilter';
import { ViewSelector } from './ViewSelector';
import { SharedView, ViewTemplate } from '../hooks/useSharedViews';
import { TopFilters, type TopFilterConfig } from './TopFilters';

interface FilterToolbarProps {
  filters: TopFilterConfig[];
  date: { startDate: string; endDate: string };
  onDateChange: (date: { startDate: string; endDate: string }) => void;
  queryParams: URLSearchParams;
  zebraRows: boolean;
  onZebraRowsChange: (value: boolean) => void;
  compactHeaders: boolean;
  onCompactHeadersChange: (value: boolean) => void;
  savedViews: any[];
  selectedViewName: string;
  onViewChange: (name: string) => void;
  onSaveView: (payload: {
    name: string;
    description?: string;
    share?: boolean;
  }) => Promise<void> | void;
  onDeleteView: () => Promise<void> | void;
  onReset: () => void;
  // New props for shared views
  sharedViews?: SharedView[];
  viewTemplates?: ViewTemplate[];
  canShareViews?: boolean;
  onSaveSharedView?: (name: string, description?: string) => Promise<void>;
  onApplySharedView?: (view: SharedView | ViewTemplate, type: 'shared' | 'template') => void;
  loading?: boolean;
  // Tenant name for export filenames
  tenantName?: string | null;
}

// Dialog for saving views with sharing option
const SaveViewDialog = memo<{
  open: boolean;
  onClose: () => void;
  onSave: (options: { name: string; description?: string; share: boolean }) => Promise<void> | void;
  canShare: boolean;
  initialName?: string;
  initialShare?: boolean;
  isUpdatingExistingView?: boolean;
}>(
  ({
    open,
    onClose,
    onSave,
    canShare,
    initialName,
    initialShare = false,
    isUpdatingExistingView,
  }) => {
    const [name, setName] = useState(initialName || '');
    const [description, setDescription] = useState('');
    const [shareView, setShareView] = useState(initialShare && canShare);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      if (!open) return;
      setName(initialName || '');
      setShareView(initialShare && canShare);
    }, [open, initialName, initialShare, canShare]);

    // Determine if we're updating an existing view
    const isUpdating = Boolean(isUpdatingExistingView ?? (initialName && initialName !== 'View 1'));

    const handleSave = async () => {
      const trimmedName = name.trim();
      if (!trimmedName || saving) return;

      setSaving(true);
      setError(null);

      try {
        await onSave({
          name: trimmedName,
          description: description.trim() || undefined,
          share: shareView && canShare,
        });

        setName('');
        setDescription('');
        setShareView(initialShare && canShare);
        onClose();
      } catch (err: any) {
        console.error('Failed to save view from dialog:', err);
        setError(err?.message || 'Unable to save view. Please try again.');
      } finally {
        setSaving(false);
      }
    };

    const handleClose = () => {
      setName('');
      setDescription('');
      setShareView(initialShare && canShare);
      setError(null);
      onClose();
    };

    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isUpdating ? `Update "${initialName}"` : 'Save New View'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="View Name"
            fullWidth
            name="view-name"
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter a name for this view"
            error={Boolean(error) && !name.trim()}
            helperText={
              Boolean(error) && !name.trim()
                ? 'A view name is required'
                : isUpdating
                  ? 'Keep the same name to update, or change it to save as new'
                  : undefined
            }
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            name="view-description"
            variant="outlined"
            multiline
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what this view is useful for"
            sx={{ mt: 2 }}
          />

          {/* What gets saved */}
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              This view will save:
            </Typography>
            <Typography
              variant="caption"
              component="div"
              sx={{ color: 'text.secondary', lineHeight: 1.6 }}
            >
              • All filters (Type Code, Line Type, Line Pay, Covered Line)
              <br />
              • Column visibility, order, sizing, and pinning
              <br />
              • Sort settings and search filters
              <br />
              • Date range and page size
              <br />• Display settings (density, compact headers, zebra rows)
            </Typography>
          </Box>

          {isUpdating && initialName && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              Saving now will refresh “{initialName}” with the current table layout, filters, and
              dates.
            </Typography>
          )}

          {canShare && (
            <FormControlLabel
              control={
                <Checkbox checked={shareView} onChange={(e) => setShareView(e.target.checked)} />
              }
              label="Share this view with all users"
              sx={{ mt: 2 }}
            />
          )}
          {shareView && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Shared views are visible to all users and will include your email as the creator.
            </Typography>
          )}
          {error && Boolean(name.trim()) && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={!name.trim() || saving}>
            {saving ? 'Saving…' : isUpdating ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

SaveViewDialog.displayName = 'SaveViewDialog';

/**
 * Memoized filter toolbar component to prevent unnecessary re-renders
 */
export const FilterToolbar = memo<FilterToolbarProps>(
  ({
    filters,
    date,
    onDateChange,
    queryParams,
    zebraRows,
    onZebraRowsChange,
    compactHeaders,
    onCompactHeadersChange,
    savedViews,
    selectedViewName,
    onViewChange,
    onSaveView,
    onDeleteView,
    onReset,
    sharedViews = [],
    viewTemplates = [],
    canShareViews = false,
    onSaveSharedView,
    onApplySharedView,
    loading = false,
    tenantName,
  }) => {
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const cleanSelectedViewName = useMemo(() => {
      if (!selectedViewName) return '';
      if (selectedViewName.startsWith('template:')) {
        const templateId = selectedViewName.replace('template:', '');
        return viewTemplates.find((t) => t.id === templateId)?.name || '';
      }
      if (selectedViewName.startsWith('shared:')) {
        const sharedId = selectedViewName.replace('shared:', '');
        return sharedViews.find((s) => s.id === sharedId)?.name || '';
      }
      return selectedViewName.replace(/^\[(Shared|Template)\]\s/, '');
    }, [selectedViewName, sharedViews, viewTemplates]);

    const isSharedSelection = Boolean(
      selectedViewName &&
        (selectedViewName.startsWith('[Shared] ') || selectedViewName.startsWith('shared:')),
    );
    const isTemplateSelection = Boolean(
      selectedViewName &&
        (selectedViewName.startsWith('[Template] ') || selectedViewName.startsWith('template:')),
    );
    const canUpdateExistingView = Boolean(
      cleanSelectedViewName && selectedViewName && !isSharedSelection && !isTemplateSelection,
    );
    const saveButtonLabel = canUpdateExistingView ? 'Update View' : 'Save View';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: '100%' }}>
        {/* Single row with all controls */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Checkboxes */}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={zebraRows}
              onChange={(e) => onZebraRowsChange(e.target.checked)}
            />
            Zebra rows
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={compactHeaders}
              onChange={(e) => onCompactHeadersChange(e.target.checked)}
            />
            Compact headers
          </label>

          {/* Date Range */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DateRangeFilter value={date} onChange={onDateChange} label="Date Range" />
            {loading && <CircularProgress size={20} thickness={5} sx={{ color: 'primary.main' }} />}
          </div>

          {/* Export Button */}
          <ExportButton
            queryParams={queryParams}
            date={date.startDate || date.endDate}
            tenantName={tenantName}
          />

          {/* Filters */}
          <TopFilters filters={filters} />

          {/* View Management */}
          <ViewSelector
            selectedViewName={selectedViewName}
            savedViews={savedViews}
            sharedViews={sharedViews}
            viewTemplates={viewTemplates}
            onViewChange={onViewChange}
            onApplySharedView={onApplySharedView}
            label="View"
          />

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <button
              style={{ height: 32, padding: '0 10px', fontSize: 13 }}
              onClick={() => setSaveDialogOpen(true)}
              title={
                canUpdateExistingView && cleanSelectedViewName
                  ? `Update "${cleanSelectedViewName}" with the latest settings`
                  : 'Save these settings as a new view'
              }
            >
              {saveButtonLabel}
            </button>
            {canUpdateExistingView && cleanSelectedViewName && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                Updates “{cleanSelectedViewName}”
              </Typography>
            )}
          </div>

          <button
            style={{ height: 32, padding: '0 10px', fontSize: 13 }}
            disabled={!selectedViewName}
            onClick={() => {
              if (!selectedViewName) return;
              onDeleteView();
            }}
          >
            Delete
          </button>

          <button
            aria-label="Reset filters"
            title="Reset filters"
            style={{ height: 32, padding: '0 10px', fontSize: 13 }}
            onClick={onReset}
          >
            Reset
          </button>

          {/* Show indicators for view types */}
          {(sharedViews.length > 0 || viewTemplates.length > 0) && (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {viewTemplates.length > 0 && (
                <Chip
                  size="small"
                  label={`${viewTemplates.length} templates`}
                  variant="outlined"
                  color="primary"
                  sx={{ height: '20px', fontSize: '0.7rem' }}
                />
              )}
              {sharedViews.length > 0 && (
                <Chip
                  size="small"
                  label={`${sharedViews.length} shared`}
                  variant="outlined"
                  color="secondary"
                  sx={{ height: '20px', fontSize: '0.7rem' }}
                />
              )}
            </Box>
          )}
        </div>

        {/* Save View Dialog */}
        <SaveViewDialog
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          onSave={async ({ name, description, share }) => {
            if (share && canShareViews && onSaveSharedView) {
              await onSaveSharedView(name, description);
            } else {
              await onSaveView({ name, description, share });
            }
            setSaveDialogOpen(false);
          }}
          canShare={canShareViews}
          initialName={cleanSelectedViewName || 'View 1'}
          initialShare={isSharedSelection}
          isUpdatingExistingView={canUpdateExistingView}
        />
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for better performance
    return (
      prevProps.date === nextProps.date &&
      prevProps.zebraRows === nextProps.zebraRows &&
      prevProps.compactHeaders === nextProps.compactHeaders &&
      prevProps.selectedViewName === nextProps.selectedViewName &&
      prevProps.canShareViews === nextProps.canShareViews &&
      prevProps.tenantName === nextProps.tenantName &&
      prevProps.queryParams.toString() === nextProps.queryParams.toString() &&
      JSON.stringify(prevProps.filters) === JSON.stringify(nextProps.filters) &&
      JSON.stringify(prevProps.savedViews) === JSON.stringify(nextProps.savedViews) &&
      JSON.stringify(prevProps.sharedViews) === JSON.stringify(nextProps.sharedViews) &&
      JSON.stringify(prevProps.viewTemplates) === JSON.stringify(nextProps.viewTemplates)
    );
  },
);

FilterToolbar.displayName = 'FilterToolbar';
